




























































































import {Vue, Component, Prop, VModel, Watch} from 'vue-property-decorator';
import CompanyDTO from "@/dto/company/CompanyDTO";
import { debounce } from 'typescript-debounce-decorator';
import CompanyService from "@/services/CompanyService";
import CompanyFilterDTO from "@/dto/company/CompanyFilterDTO";
import PortalInput from "@/components/common/PortalInput.vue";
import Companies from "@/components/taxation/business/Companies.vue";
import {processError} from "@/utils/ComponentUtils";
import {namespace} from "vuex-class";

const AppModule = namespace("App");
export type OptionType = "ALL" | "NAME" | "EMAIL" | "EIN"

@Component({
	components: {Companies, PortalInput}
})
export default class CompanySearch extends Vue {
	@Prop({default: 'ALL'})
	private options!: OptionType | Array<OptionType>

	@Prop({default: "company"})
	private name!: string

	@VModel({type: CompanyDTO})
	private selectedCompany!: CompanyDTO | null;

	@AppModule.State
	private isMobile!: boolean;

	private filter = new CompanyFilterDTO({pageSize: 8});

	private companies: Array<CompanyDTO> = [];

	private showList = false;

	private isLoading = false;

	private currentCompany: CompanyDTO | null = null;

	private viewportWidth: number = typeof window !== 'undefined' ? window.innerWidth : 0;

	private resultsPlacement: 'side' | 'bottom' = 'bottom';

	private fieldsBlockWidth = 0;

	private layoutWidth = 0;

	public mounted(): void {
		this.handleSelectedCompanyChange(this.selectedCompany);
		if (typeof window !== 'undefined') {
			window.addEventListener('resize', this.onResize, { passive: true });
		}
	}

	public beforeDestroy(): void {
		if (typeof window !== 'undefined') {
			window.removeEventListener('resize', this.onResize);
		}
	}

	@debounce(500)
	search(){
		if (this.showSummary) {
			return;
		}
		this.isLoading = true;
		this.showList = true;
		this.$emit("filter", this.filter)
		CompanyService.getByFilter(this.filter).then(
			ok => {
				this.companies = (ok?.data?.data) || [];
				this.isLoading = false;
				this.$nextTick(() => this.evaluateResultsPlacement());
			},
			err => {
				processError(err, this)
				this.companies = [];
				this.showList = false;
				this.resultsPlacement = 'bottom';
				this.isLoading = false;
			}
		)
	}

	selectCompany(c: CompanyDTO){
		this.showList = false;
		this.currentCompany = c;
		this.syncFilterFromCompany(c);
		this.$emit('input', c);
		this.$emit("change", c);
	}

	resetSelection(): void {
		this.$emit('input', null);
		this.$emit("change", null);
		this.companies = [];
		this.showList = false;
		this.currentCompany = null;
		this.resultsPlacement = 'bottom';
		this.resetFilter();
	}

	get showSummary(): boolean {
		return !!this.currentCompany;
	}

	get shouldShowList(): boolean {
		return !this.showSummary && this.showList && !!(this.filter.email || this.filter.ein || this.filter.companyName);
	}

	get showEmailInList(): boolean {
		return this.shouldShowField('EMAIL');
	}

	get showEinInList(): boolean {
		return this.shouldShowField('EIN');
	}

	get isDesktop(): boolean {
		return this.viewportWidth >= 960;
	}

	get selectedCompanyName(): string {
		return this.currentCompany ? this.currentCompany.name : 'Компания не выбрана';
	}

	get selectedCompanyEin(): string | null {
		return this.currentCompany ? this.currentCompany.ein : null;
	}

	get selectedCompanyEmail(): string | null {
		return this.currentCompany ? this.currentCompany.email : null;
	}

	get layoutClasses(): Record<string, boolean> {
		return {
			'company-search__layout': true,
			'company-search__layout--with-results': this.shouldShowList,
			'company-search__layout--desktop': this.isDesktop,
			'company-search__layout--mobile': !this.isDesktop,
			'company-search__layout--side': this.isResultsBeside,
			'company-search__layout--bottom': !this.isResultsBeside
		};
	}

	get isResultsBeside(): boolean {
		return this.resultsPlacement === 'side';
	}

	get resultsClasses(): Record<string, boolean> {
		return {
			'company-search__results': true,
			'company-search__results--side': this.isResultsBeside,
			'company-search__results--bottom': !this.isResultsBeside
		};
	}

	get layoutInlineStyles(): Record<string, string> {
		if (!this.shouldShowList) {
			return {};
		}
		if (this.isResultsBeside) {
			return {};
		}
		return {
		};
	}

	get resultsInlineStyles(): Record<string, string> {
		if (this.isResultsBeside) {
			return {
				width: `${this.resultsPanelWidth}px`,
				left: `${this.fieldsBlockWidth + this.sideResultsGap}px`,
				right: 'auto',
				top: '0',
				maxHeight: `${this.resultsPanelHeight}px`
			};
		}
		return {
			maxHeight: `min(${this.resultsPanelHeight}px, 60vh)`
		};
	}

	get resultsPanelWidth(): number {
		const base = this.isDesktop ? Math.floor(this.viewportWidth * 0.28) : Math.floor(this.viewportWidth * 0.92);
		return Math.max(280, Math.min(base, 360));
	}

	get resultsPanelHeight(): number {
		return this.isDesktop ? 420 : 360;
	}

	get sideResultsGap(): number {
		return Math.max(16, Math.min(32, Math.floor(this.viewportWidth * 0.02)));
	}

	get fieldsInlineStyles(): Record<string, string> {
		if (!this.isResultsBeside || !this.shouldShowList || !this.isMobile) {
			return {};
		}
		const availableWidth = Math.max(this.layoutWidth - this.resultsPanelWidth - this.sideResultsGap, 0);
		return {
			maxWidth: `${availableWidth}px`,
			width: '100%'
		};
	}

	shouldShowField(option: OptionType): boolean {
		if (this.options === 'ALL') {
			return true;
		}
		if (Array.isArray(this.options)) {
			return this.options.includes(option);
		}
		return this.options === option;
	}

	@Watch('selectedCompany', { immediate: true })
	private handleSelectedCompanyChange(value: CompanyDTO | null): void {
		if (value) {
			this.currentCompany = value;
			this.syncFilterFromCompany(value);
			this.showList = false;
			this.resultsPlacement = 'bottom';
		} else {
			this.currentCompany = null;
			this.resetFilter();
		}
	}

	@Watch('shouldShowList')
	private handleShouldShowListChange(value: boolean): void {
		if (value) {
			this.$nextTick(() => this.evaluateResultsPlacement());
		} else {
			this.resultsPlacement = 'bottom';
		}
	}

	private syncFilterFromCompany(company: CompanyDTO): void {
		this.filter.companyName = company.name;
		this.filter.ein = company.ein;
		this.filter.email = company.email;
	}

	private resetFilter(): void {
		const defaults = new CompanyFilterDTO({pageSize: 8});
		this.filter.searchQuery = defaults.searchQuery;
		this.filter.companyName = defaults.companyName;
		this.filter.email = defaults.email;
		this.filter.ein = defaults.ein;
		this.filter.isClient = defaults.isClient;
		this.filter.orderField = defaults.orderField;
		this.filter.orderDirection = defaults.orderDirection;
		this.filter.pageNumber = defaults.pageNumber;
		this.filter.pageSize = defaults.pageSize;
	}

	private onResize = (): void => {
		if (typeof window === 'undefined') {
			return;
		}
		this.viewportWidth = window.innerWidth;
		if (this.shouldShowList) {
			this.evaluateResultsPlacement();
		}
	};

	private evaluateResultsPlacement(): void {
		if (!this.shouldShowList) {
			this.resultsPlacement = 'bottom';
			return;
		}

		const layout = this.layoutElement;
		const fields = this.fieldsElement;

		if (!layout || !fields) {
			this.resultsPlacement = this.isDesktop ? 'side' : 'bottom';
			return;
		}

		this.layoutWidth = layout.offsetWidth;
		this.fieldsBlockWidth = fields.offsetWidth;
		const fieldsRect = fields.getBoundingClientRect();
		const availableViewportRight = Math.max(this.viewportWidth - fieldsRect.right, 0);
		const needsSide = this.isDesktop && availableViewportRight >= this.resultsPanelWidth + this.sideResultsGap;

		this.resultsPlacement = needsSide ? 'side' : 'bottom';
	}

	private get layoutElement(): HTMLElement | null {
		const ref = this.$refs.layoutEl as HTMLElement | undefined;
		return ref || null;
	}

	private get fieldsElement(): HTMLElement | null {
		const ref = this.$refs.fieldsEl as HTMLElement | undefined;
		return ref || null;
	}

}
